import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Calendar, Clock, MapPin, Users, FileText, Image, Type, CheckCircle, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const OAUTH_POPUP_WIDTH = 600;
const OAUTH_POPUP_HEIGHT = 700;

// Single source of truth: every API call carries the session_id cookie.
// Identity is never read from URLs, request bodies, or localStorage.
axios.defaults.withCredentials = true;

function CalendarApp() {
  // user === null means "not signed in" (or status check hasn't completed
  // yet — distinguished by `loading` below). The shape is whatever
  // GET /api/web/user/status returns: { connected, email, calendar_connected, google_email }.
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upload');
  const [busy, setBusy] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [uploadSource, setUploadSource] = useState(null);
  const [eventForm, setEventForm] = useState(getEmptyForm());
  const [calendars, setCalendars] = useState([]);
  const [events, setEvents] = useState([]);
  const [guestInput, setGuestInput] = useState('');

  // Hits the cookie-authenticated user-status endpoint. 200 -> we're
  // signed in; 401 (or any error) -> we're not. No identifier passed.
  const refreshUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/web/user/status`);
      setUser(response.data);
    } catch (_) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCalendars = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/web/calendars`);
      setCalendars(response.data.calendars);
    } catch (_) { /* calendars optional */ }
  }, []);

  const loadEvents = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/web/events`);
      setEvents(response.data.events);
    } catch (_) { /* events optional */ }
  }, []);

  // On mount: figure out who we are. Also listen for the OAuth-success
  // postMessage that the /api/oauth/web/callback popup sends when sign-in
  // completes — Phase 3 stripped the email payload, so we just need the
  // signal to refetch user status (the cookie is already set by then).
  useEffect(() => {
    refreshUser();

    const handleMessage = (event) => {
      // Tighten: only accept oauth_success from our own origin.
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'oauth_success') {
        refreshUser();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [refreshUser]);

  // Once the user is signed in AND has Google calendar connected,
  // populate the calendars + events lists.
  useEffect(() => {
    if (user?.calendar_connected) {
      loadCalendars();
      loadEvents();
    }
  }, [user, loadCalendars, loadEvents]);

  const connectCalendar = () => {
    // No identity in the URL — Phase 3 backend rejects ?email= and
    // derives the email from Google's userinfo response instead.
    const authUrl = `${API}/oauth/web/connect`;
    const left = (window.screen.width - OAUTH_POPUP_WIDTH) / 2;
    const top = (window.screen.height - OAUTH_POPUP_HEIGHT) / 2;

    const popup = window.open(
      authUrl, 'GoogleCalendarAuth',
      `width=${OAUTH_POPUP_WIDTH},height=${OAUTH_POPUP_HEIGHT},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`
    );

    if (!popup) {
      alert('Please allow popups for this site, or try opening in a new tab');
      window.open(authUrl, '_blank');
      return;
    }

    // Belt-and-braces: postMessage from the popup will trigger
    // refreshUser via the global listener, but if the message is missed
    // (browser quirk, slow render) we also retry once the popup closes.
    const checkPopup = setInterval(() => {
      try {
        if (popup.closed) {
          clearInterval(checkPopup);
          refreshUser();
        }
      } catch (_) {
        // Cross-origin while popup is on Google's domain — ignore.
      }
    }, 500);
  };

  const signOut = async () => {
    try {
      await axios.post(`${API}/web/logout`);
    } catch (_) {
      // Logout is idempotent on the backend; even if the request fails
      // we want the local UI to forget about the session immediately.
    }
    setUser(null);
    setCalendars([]);
    setEvents([]);
    window.location.reload();
  };

  const handleFileUpload = async (event, type) => {
    const file = event.target.files[0];
    if (!file) return;
    setBusy(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const endpoint = type === 'pdf' ? '/web/upload/pdf' : '/web/upload/image';
      const response = await axios.post(`${API}${endpoint}`, formData);
      setExtractedData(response.data.extracted);
      setUploadSource(response.data.source);
      populateFormFromExtracted(response.data.extracted);
      setActiveTab('preview');
    } catch (error) {
      alert('Error processing file: ' + (error.response?.data?.detail || error.message));
    }
    setBusy(false);
  };

  const handleTextExtraction = async (text) => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const response = await axios.post(`${API}/web/extract/text`, { text });
      setExtractedData(response.data.extracted);
      setUploadSource('text');
      populateFormFromExtracted(response.data.extracted);
      setActiveTab('preview');
    } catch (error) {
      alert('Error extracting from text: ' + (error.response?.data?.detail || error.message));
    }
    setBusy(false);
  };

  const populateFormFromExtracted = (extracted) => {
    setEventForm({
      subject: extracted.event_name || '',
      date: extracted.date || '',
      time: extracted.time || '',
      duration: 60,
      location: extracted.location || '',
      description: extracted.raw_text || '',
      dress_code: extracted.dress_code || '',
      calendar_id: 'primary',
      guests: []
    });
  };

  const handleCreateEvent = async () => {
    if (!eventForm.subject || !eventForm.date || !eventForm.time) {
      alert('Please fill in event title, date, and time');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/web/events/create`, eventForm);
      alert('Event created successfully!');
      loadEvents();
      resetForm();
      setActiveTab('events');
    } catch (error) {
      alert('Error creating event: ' + (error.response?.data?.detail || error.message));
    }
    setBusy(false);
  };

  const addGuest = () => {
    if (guestInput && guestInput.includes('@')) {
      setEventForm({ ...eventForm, guests: [...eventForm.guests, guestInput.trim()] });
      setGuestInput('');
    }
  };

  const removeGuest = (email) => {
    setEventForm({ ...eventForm, guests: eventForm.guests.filter(g => g !== email) });
  };

  const resetForm = () => {
    setEventForm(getEmptyForm());
    setExtractedData(null);
    setUploadSource(null);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <LoginScreen connectCalendar={connectCalendar} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50" data-testid="calendar-app">
      <AppHeader user={user} onSignOut={signOut} />
      <TabNav activeTab={activeTab} setActiveTab={setActiveTab} />

      <main className="max-w-7xl mx-auto px-4 py-8">
        {activeTab === 'upload' && (
          <UploadTab loading={busy} handleFileUpload={handleFileUpload} handleTextExtraction={handleTextExtraction} />
        )}
        {activeTab === 'manual' && (
          <div className="bg-white rounded-xl shadow-md p-8 max-w-3xl mx-auto" data-testid="manual-entry-form">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Create Event Manually</h2>
            <EventForm {...{ eventForm, setEventForm, calendars, guestInput, setGuestInput, addGuest, removeGuest, handleCreateEvent, loading: busy }} />
          </div>
        )}
        {activeTab === 'preview' && extractedData && (
          <PreviewTab
            extractedData={extractedData} uploadSource={uploadSource}
            eventForm={eventForm} setEventForm={setEventForm} calendars={calendars}
            guestInput={guestInput} setGuestInput={setGuestInput} addGuest={addGuest}
            removeGuest={removeGuest} handleCreateEvent={handleCreateEvent} loading={busy}
            onCancel={resetForm}
          />
        )}
        {activeTab === 'events' && (
          <EventsTab events={events} loadEvents={loadEvents} />
        )}
      </main>
    </div>
  );
}

function getEmptyForm() {
  return { subject: '', date: '', time: '', duration: 60, location: '', description: '', dress_code: '', calendar_id: 'primary', guests: [] };
}

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center" data-testid="loading-screen">
      <Calendar className="w-12 h-12 text-purple-600 animate-pulse" />
    </div>
  );
}

function LoginScreen({ connectCalendar }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center p-4" data-testid="login-screen">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <Calendar className="w-16 h-16 text-purple-600 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Invitation to Calendar</h1>
          <p className="text-gray-600">Sign in with Google to get started</p>
        </div>
        <button
          onClick={connectCalendar}
          className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors"
          data-testid="connect-calendar-button"
        >
          Sign in with Google
        </button>
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h3 className="font-semibold text-blue-900 mb-2">Features:</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>Upload PDF invitations</li>
            <li>Scan invitation photos</li>
            <li>Paste invitation text</li>
            <li>Gujarati & multilingual support</li>
            <li>Add guest invitations</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function AppHeader({ user, onSignOut }) {
  return (
    <header className="bg-white shadow-sm border-b">
      <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Calendar className="w-8 h-8 text-purple-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Invitation to Calendar</h1>
            <p className="text-sm text-gray-500">{user?.email}</p>
          </div>
        </div>
        <div className="flex items-center space-x-4">
          {user?.is_admin && (
            <a
              href="/admin"
              className="text-sm text-purple-700 hover:text-purple-900"
              data-testid="admin-link"
            >
              Admin
            </a>
          )}
          <button
            onClick={onSignOut}
            className="text-sm text-red-600 hover:text-red-700"
            data-testid="sign-out-button"
          >
            Sign out
          </button>
        </div>
      </div>
    </header>
  );
}

function TabNav({ activeTab, setActiveTab }) {
  const tabs = [
    { key: 'upload', label: 'Upload' },
    { key: 'manual', label: 'Manual Entry' },
    { key: 'preview', label: 'Preview & Edit' },
    { key: 'events', label: 'My Events' },
  ];
  return (
    <div className="bg-white border-b">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.key ? 'border-purple-600 text-purple-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid={`tab-${tab.key}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function UploadTab({ loading, handleFileUpload, handleTextExtraction }) {
  const cards = [
    { type: 'pdf', icon: FileText, color: 'red', title: 'Upload PDF', desc: 'Extract event details from PDF invitations', accept: '.pdf', label: 'Choose PDF' },
    { type: 'image', icon: Image, color: 'green', title: 'Upload Photo', desc: 'Scan invitation cards with AI OCR', accept: 'image/*', label: 'Choose Image' },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {cards.map((card) => (
        <div key={card.type} className="bg-white rounded-xl shadow-md p-6">
          <div className="text-center">
            <div className={`w-16 h-16 bg-${card.color}-100 rounded-full flex items-center justify-center mx-auto mb-4`}>
              <card.icon className={`w-8 h-8 text-${card.color}-600`} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{card.title}</h3>
            <p className="text-sm text-gray-600 mb-4">{card.desc}</p>
            <label className="block">
              <input type="file" accept={card.accept} onChange={(e) => handleFileUpload(e, card.type)} className="hidden" disabled={loading} />
              <div className={`cursor-pointer bg-${card.color}-600 text-white py-2 px-4 rounded-lg hover:bg-${card.color}-700 transition-colors`} data-testid={`upload-${card.type}-button`}>
                {loading ? 'Processing...' : card.label}
              </div>
            </label>
          </div>
        </div>
      ))}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Type className="w-8 h-8 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Paste Text</h3>
          <p className="text-sm text-gray-600 mb-4">Extract from invitation text</p>
          <button
            onClick={() => { const text = prompt('Paste your invitation text here:'); if (text) handleTextExtraction(text); }}
            disabled={loading}
            className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300"
            data-testid="paste-text-button"
          >
            {loading ? 'Processing...' : 'Paste Text'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PreviewTab({ extractedData, uploadSource, onCancel, ...formProps }) {
  return (
    <div className="bg-white rounded-xl shadow-md p-8 max-w-3xl mx-auto" data-testid="preview-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Review & Edit Event</h2>
        <span className="text-sm bg-purple-100 text-purple-700 px-3 py-1 rounded-full">From {uploadSource}</span>
      </div>
      <div className="mb-6 p-4 bg-blue-50 rounded-lg">
        <h3 className="font-semibold text-blue-900 mb-2">AI Extracted:</h3>
        <div className="text-sm text-blue-800 space-y-1">
          <p><strong>Event:</strong> {extractedData.event_name || 'N/A'}</p>
          <p><strong>Date:</strong> {extractedData.date || 'N/A'}</p>
          <p><strong>Time:</strong> {extractedData.time || 'N/A'}</p>
          <p><strong>Location:</strong> {extractedData.location || 'N/A'}</p>
          {extractedData.dress_code && <p><strong>Dress Code:</strong> {extractedData.dress_code}</p>}
        </div>
      </div>
      <EventForm {...formProps} showCancel onCancel={onCancel} />
    </div>
  );
}

function EventsTab({ events, loadEvents }) {
  return (
    <div className="bg-white rounded-xl shadow-md p-8" data-testid="events-list">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Upcoming Events</h2>
        <button onClick={loadEvents} className="text-purple-600 hover:text-purple-700 text-sm font-medium" data-testid="refresh-events-button">
          Refresh
        </button>
      </div>
      {events.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <p>No upcoming events</p>
        </div>
      ) : (
        <div className="space-y-4">
          {events.map((event) => (
            <div key={event.id} className="border-l-4 border-purple-500 bg-purple-50 p-4 rounded-r-lg" data-testid={`event-${event.id}`}>
              <h3 className="font-semibold text-gray-900 text-lg">{event.summary}</h3>
              <div className="mt-2 space-y-1 text-sm text-gray-700">
                <p className="flex items-center"><Clock className="w-4 h-4 mr-2" />{new Date(event.start.dateTime || event.start.date).toLocaleString()}</p>
                {event.location && <p className="flex items-center"><MapPin className="w-4 h-4 mr-2" />{event.location}</p>}
                {event.attendees?.length > 0 && <p className="flex items-center"><Users className="w-4 h-4 mr-2" />{event.attendees.length} guest(s)</p>}
              </div>
              {event.htmlLink && (
                <a href={event.htmlLink} target="_blank" rel="noopener noreferrer" className="inline-block mt-3 text-purple-600 hover:text-purple-700 text-sm font-medium">
                  View in Google Calendar
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EventForm({ eventForm, setEventForm, calendars, guestInput, setGuestInput, addGuest, removeGuest, handleCreateEvent, loading, showCancel, onCancel }) {
  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Event Title *</label>
        <input type="text" value={eventForm.subject} onChange={(e) => setEventForm({ ...eventForm, subject: e.target.value })}
          placeholder="Team Meeting" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" data-testid="event-title-input" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Date *</label>
          <input type="date" value={eventForm.date} onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" data-testid="event-date-input" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Time *</label>
          <input type="time" value={eventForm.time} onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" data-testid="event-time-input" />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Duration (minutes)</label>
        <input type="number" value={eventForm.duration} onChange={(e) => setEventForm({ ...eventForm, duration: parseInt(e.target.value) })}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
        <input type="text" value={eventForm.location} onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
          placeholder="Conference Room A or https://zoom.us/..." className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
        <textarea value={eventForm.description} onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
          placeholder="Event details..." rows={3} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Dress Code</label>
        <input type="text" value={eventForm.dress_code} onChange={(e) => setEventForm({ ...eventForm, dress_code: e.target.value })}
          placeholder="Business Casual" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Calendar</label>
        <select value={eventForm.calendar_id} onChange={(e) => setEventForm({ ...eventForm, calendar_id: e.target.value })}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" data-testid="calendar-select">
          {calendars.map((cal) => (<option key={cal.id} value={cal.id}>{cal.is_primary ? '* ' : ''}{cal.name}</option>))}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Guests</label>
        <div className="flex space-x-2 mb-2">
          <input type="email" value={guestInput} onChange={(e) => setGuestInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addGuest())}
            placeholder="guest@email.com" className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" data-testid="guest-email-input" />
          <button onClick={addGuest} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700" data-testid="add-guest-button">Add</button>
        </div>
        {eventForm.guests.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {eventForm.guests.map((guest) => (
              <span key={guest} className="inline-flex items-center bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm" data-testid={`guest-${guest}`}>
                {guest}
                <button onClick={() => removeGuest(guest)} className="ml-2"><X className="w-4 h-4" /></button>
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="flex space-x-4 pt-4">
        <button onClick={handleCreateEvent} disabled={loading}
          className="flex-1 bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
          data-testid="create-event-button">
          {loading ? 'Creating...' : (<><CheckCircle className="w-5 h-5 mr-2" />Create Event</>)}
        </button>
        {showCancel && (
          <button onClick={onCancel} className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors" data-testid="cancel-button">
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

export default CalendarApp;
